import FeatureGates from '@atlaskit/feature-gate-js-client';
import { getLocalStorageItemAsString, setLocalStorageItem } from '@atlassian/search-common';

import { extractTraceInfoFromResponse, type NetworkTraceInfo } from '../../network-trace-info';
import { getInstance, type ICache } from '../common/cache';

import { SEARCH_COMBINED_CONFIG_CACHE_KEY } from './constants';
import { getSearchClientStorageAdapterFactory } from './registerStorageAdapter';
import type { PersistedStorageAdapter } from './storageAdapter';

interface RefreshOnAccessArgs {
	/**
	 * Is the identifier for the name in the local storage.
	 */
	name: string;
}

export type CacheAccessState = {
	refreshTriggered: boolean;
	hit: boolean;
};
export type CacheAnalyticsCallback = (state: CacheAccessState) => Promise<void>;

export type NetworkCallbacks = {
	onStart: () => void;
	onSuccess: (traceInfo?: NetworkTraceInfo) => void;
	onError: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => void;
};

/**
 * Cache implementation which always returns the persited response (if available).
 * But refreshes the response everytime its requested, so that next time onwards the data is up to date.
 *
 * Note - This will also return an expired result. This strategy should be used only in scenarios where:
 * 1. The data changes rarely.
 * 2. Its fine to serve the expired result to the user.
 */
export const promiseCache: <T>(args: ICache & RefreshOnAccessArgs) => {
	get: (
		key: string,
		returnExpired?: boolean,
	) => {
		storedValue: Promise<T> | undefined;
		isExpired: boolean;
	};
	refreshOnAccessDecorator: ({
		key,
		supplier,
		returnExpired,
		onlyRefreshIfExpired,
		expiration,
		analyticsCallback,
		networkCallbacks,
		serveResolvedDuringRefresh,
		traceInfoExtractor,
	}: {
		key: string;
		supplier: () => Promise<T>;
		returnExpired?: boolean;
		onlyRefreshIfExpired?: boolean;
		expiration?: number;
		analyticsCallback?: CacheAnalyticsCallback;
		networkCallbacks?: NetworkCallbacks;
		serveResolvedDuringRefresh?: boolean;
		/**
		 * Optional custom trace info extractor for REST endpoints.
		 * When provided, this is called instead of extractTraceInfoFromResponse()
		 * to obtain requestId/traceId for network VitalStats events.
		 */
		traceInfoExtractor?: () => NetworkTraceInfo | undefined;
	}) => Promise<T> | (T & ({} | null));
	clear: (key?: string) => void;
	update: (key: string, updater: (current: T) => T, expiration?: number) => Promise<boolean>;
	seed: (key: string, value: T, expiration?: number) => Promise<void>;
} = <T>(args: ICache & RefreshOnAccessArgs) => {
	const { name } = args;
	let cacheInstance: ReturnType<typeof getInstance<T>> | null = null;
	let resolvedStorage: PersistedStorageAdapter | null = null;

	// During refresh, the main cache will hold pending promises so we need a way to access the last resolved value
	// This cache can return these values while the refresh is in progress
	const resolvedValueCache = new Map<string, T>();

	/**
	 * NOTE: Scope control (QS-9209)
	 *
	 * The storage adapter system is introduced specifically so the combined search config cache
	 * can participate in host-provided persistence (e.g., Confluence SSR local storage).
	 *
	 * Two experiments control this behavior:
	 * - `search_combined_config`: Enables the combined config endpoint (QS-9091)
	 * - `search_config_ssr`: Enables the SSR persistence adapter system (QS-9209)
	 *
	 * Both must be enabled for the adapter system to be used. This allows:
	 * - Combined config without SSR persistence (search_combined_config=ON, search_config_ssr=OFF)
	 * - Full SSR persistence (search_combined_config=ON, search_config_ssr=ON)
	 */
	const COMBINED_CONFIG_PERSISTED_CACHE_KEY = SEARCH_COMBINED_CONFIG_CACHE_KEY;

	const defaultLocalStorageAdapter: PersistedStorageAdapter = {
		getItem: (key: string) => getLocalStorageItemAsString(key),
		setItem: (key: string, value: string) => setLocalStorageItem(key, value),
	};

	function isCombinedConfigEnabled(): boolean {
		return (
			FeatureGates.getExperimentValue<boolean>('search_combined_config', 'isEnabled', false) ===
			true
		);
	}

	function isConfigSSREnabled(): boolean {
		return (
			FeatureGates.getExperimentValue<boolean>('search_config_ssr', 'isEnabled', false) === true
		);
	}

	function shouldUseAdapterSystemForThisCache(): boolean {
		return (
			name === COMBINED_CONFIG_PERSISTED_CACHE_KEY &&
			isCombinedConfigEnabled() &&
			isConfigSSREnabled()
		);
	}

	const getCacheInstance = () => {
		/**
		 * We lazily create the cache instance to avoid the issue in SSR
		 * where the localStorage isn't available.
		 */
		if (!cacheInstance) {
			cacheInstance = getInstance<T>(args);

			// Resolve persisted state lazily (SSR-safe)
			let persistedState: string | null = null;
			try {
				if (shouldUseAdapterSystemForThisCache()) {
					const factory = getSearchClientStorageAdapterFactory();
					resolvedStorage = factory?.() ?? defaultLocalStorageAdapter;
					persistedState = resolvedStorage.getItem(name);
				} else {
					persistedState = getLocalStorageItemAsString(name);
				}
			} catch {
				// Error retrieving persisted state - continue without it
			}

			if (persistedState) {
				// Populate the resolved value cache with the contents of local storage
				const object = cacheInstance.deserialize(persistedState);
				const serveResolvedDuringRefreshEnabled = FeatureGates.getExperimentValue<boolean>(
					'search_cache_serve_resolved_during_refresh',
					'isEnabled',
					false,
				);
				if (object && serveResolvedDuringRefreshEnabled) {
					Object.entries(object).forEach(([key, entry]) => {
						if (entry && 'result' in entry) {
							resolvedValueCache.set(key, entry.result as T);
						}
					});
				}
			}
		}
		return cacheInstance;
	};

	const get = (key: string, returnExpired?: boolean) => {
		const storedValue = getCacheInstance().get(key, returnExpired ?? true); // fetch expired result by default
		const isExpired = getCacheInstance().isExpired(key);

		return { storedValue, isExpired };
	};

	const set = async (key: string, result: Promise<T>, expiration?: number) => {
		getCacheInstance().set(key, result, expiration);
		try {
			const serveResolvedDuringRefreshEnabled = FeatureGates.getExperimentValue<boolean>(
				'search_cache_serve_resolved_during_refresh',
				'isEnabled',
				false,
			);
			if (serveResolvedDuringRefreshEnabled) {
				const resolvedValue = await result;
				resolvedValueCache.set(key, resolvedValue);
			}
			const serialized = await getCacheInstance().serialize();
			if (serialized) {
				if (shouldUseAdapterSystemForThisCache()) {
					const adapter =
						resolvedStorage ??
						getSearchClientStorageAdapterFactory()?.() ??
						defaultLocalStorageAdapter;
					adapter.setItem(name, serialized);
				} else {
					setLocalStorageItem(name, serialized);
				}
			}
		} catch {
			// noop - an exception in the result promise or serialisation should be ignored so we dont throw unhandled exceptions
		}
	};

	const update = async (
		key: string,
		updater: (current: T) => T,
		expiration?: number,
	): Promise<boolean> => {
		// Read remaining TTL before get(), which removes expired entries from the map
		const remainingTtl = expiration ?? getCacheInstance().getRemainingTtl(key);
		const { storedValue } = get(key, true);
		if (!storedValue) {
			return false;
		}
		try {
			const currentValue = await storedValue;
			const updatedValue = updater(currentValue);
			await set(key, Promise.resolve(updatedValue), remainingTtl || undefined);
			return true;
		} catch {
			return false;
		}
	};

	const clear = (key?: string): void => {
		const serveResolvedDuringRefreshEnabled = FeatureGates.getExperimentValue<boolean>(
			'search_cache_serve_resolved_during_refresh',
			'isEnabled',
			false,
		);
		if (serveResolvedDuringRefreshEnabled) {
			if (key) {
				resolvedValueCache.delete(key);
			} else {
				resolvedValueCache.clear();
			}
		}

		getCacheInstance().clear(key);
	};

	const refreshOnAccessDecorator = ({
		key,
		supplier,
		returnExpired,
		onlyRefreshIfExpired,
		expiration,
		analyticsCallback,
		networkCallbacks,
		serveResolvedDuringRefresh,
		traceInfoExtractor,
	}: {
		key: string;
		supplier: () => Promise<T>;
		returnExpired?: boolean;
		onlyRefreshIfExpired?: boolean;
		expiration?: number;
		analyticsCallback?: CacheAnalyticsCallback;
		networkCallbacks?: NetworkCallbacks;
		serveResolvedDuringRefresh?: boolean;
		/**
		 * Optional custom trace info extractor for REST endpoints.
		 * When provided, this is called instead of extractTraceInfoFromResponse()
		 * to obtain requestId/traceId for network VitalStats events.
		 */
		traceInfoExtractor?: () => NetworkTraceInfo | undefined;
	}) => {
		const { storedValue, isExpired } = get(key, returnExpired);

		const shouldRefresh = onlyRefreshIfExpired ? isExpired : true;

		const experimentEnabled = FeatureGates.getExperimentValue<boolean>(
			'search_cache_serve_resolved_during_refresh',
			'isEnabled',
			false,
		);

		// Require both experiment to be enabled and the caller to opt in via parameter
		const serveResolvedDuringRefreshEnabled = experimentEnabled && serveResolvedDuringRefresh;

		if (!storedValue || shouldRefresh) {
			// Wrap the supplier to track network calls
			const trackedSupplier = async () => {
				networkCallbacks?.onStart();
				try {
					const result = await supplier();
					const traceInfo = traceInfoExtractor
						? traceInfoExtractor()
						: extractTraceInfoFromResponse(result);
					networkCallbacks?.onSuccess(traceInfo);
					return result;
				} catch (error: any) {
					const errorName = error?.name || 'UnknownError';
					const errorMessage = error?.message || 'An unknown error occurred';
					networkCallbacks?.onError(errorName, errorMessage, undefined);
					throw error;
				}
			};

			const apiPromise = trackedSupplier();
			set(key, apiPromise, expiration);

			// If there was already a stored value prior to refreshing, return it
			if (storedValue) {
				if (analyticsCallback) {
					analyticsCallback({ refreshTriggered: true, hit: true });
				}
				// Return resolved value if experiment is enabled and we have it cached
				if (serveResolvedDuringRefreshEnabled) {
					const resolvedValue = resolvedValueCache.get(key);
					if (resolvedValue !== undefined) {
						return resolvedValue;
					}
				}
				return storedValue;
			} else {
				if (analyticsCallback) {
					analyticsCallback({ refreshTriggered: true, hit: false });
				}
				return apiPromise;
			}
		} else {
			if (analyticsCallback) {
				analyticsCallback({ refreshTriggered: false, hit: true });
			}
			// Return resolved value if experiment is enabled and we have it cached
			if (serveResolvedDuringRefreshEnabled) {
				const resolvedValue = resolvedValueCache.get(key);
				if (resolvedValue !== undefined) {
					return resolvedValue;
				}
			}
			return storedValue;
		}
	};

	const seed = (key: string, value: T, expiration?: number) =>
		set(key, Promise.resolve(value), expiration);

	return {
		get,
		refreshOnAccessDecorator,
		clear,
		update,
		seed,
	};
};
